import React from "react";
import { formatSuiteSectionTitle } from "utils/suiteDisplayUtils";
import {
  formatBusinessDaysHoursDisplay,
  formatFloorForProposal,
} from "../businessHoursPerDayUtils";

const WORKSTUDIO_LOGO_URL =
  "https://sworks-sg-prod.s3.ap-south-1.amazonaws.com/invoice_logo/workstudio_logo.png";
const SMARTWORKS_LOGO_URL =
  "https://smartwork-sg-uat.s3.ap-southeast-1.amazonaws.com/invoice_logo/sw_logo.svg";

/**
 * Head-terms slide for PDF-aligned preview: PROPOSAL + logo (SW: Workstudio + gradient; GEC/KBT: Smartworks).
 */
export function ProposalHeadTermsSlide({
  templateKey,
  createdProposal,
  rowvalue,
  totalHour,
  selectedRange,
}) {
  const isSw = templateKey === "sw";
  const black = "#111";

  const table = (
    <ProposalTermsTable
      createdProposal={createdProposal}
      rowvalue={rowvalue}
      totalHour={totalHour}
      selectedRange={selectedRange}
    />
  );

  if (isSw) {
    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 9",
          margin: "0 0 8px",
          backgroundColor: "#fff",
          overflow: "hidden",
          fontFamily: '"Poppins", sans-serif',
          color: black,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `
              linear-gradient(135deg, rgba(255,215,0,0.06) 0%, transparent 42%),
              linear-gradient(225deg, rgba(245,230,200,0.35) 0%, transparent 38%),
              linear-gradient(25deg, transparent 55%, rgba(245,230,200,0.2) 100%)`,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            padding:
              "clamp(14px, 2.5vw, 28px) clamp(16px, 2.8vw, 36px) clamp(12px, 2vw, 22px)",
            boxSizing: "border-box",
            zIndex: 1,
          }}
        >
          <header
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 14,
              flexShrink: 0,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  lineHeight: 1,
                }}
              >
                PROPOSAL
              </div>
              <div
                style={{
                  height: 1,
                  width: 120,
                  background: black,
                  marginTop: 6,
                }}
              />
            </div>
            <img
              src={WORKSTUDIO_LOGO_URL}
              alt="Workstudio"
              style={{
                height: 44,
                width: "auto",
                maxWidth: 220,
                display: "block",
                objectFit: "contain",
              }}
            />
          </header>
          <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto" }}>
            {table}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        margin: "0 0 8px",
        backgroundColor: "#fff",
        overflow: "hidden",
        boxSizing: "border-box",
        fontFamily: '"Poppins", sans-serif',
        color: "#000",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          padding:
            "clamp(12px, 2.2vw, 28px) clamp(14px, 2.5vw, 32px) clamp(10px, 1.5vw, 20px)",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 29,
              fontWeight: 800,
              letterSpacing: "0.02em",
              lineHeight: 1.1,
            }}
          >
            PROPOSAL
          </h2>
          <img
            src={SMARTWORKS_LOGO_URL}
            alt="Smartworks"
            style={{
              height: 40,
              width: "auto",
              maxWidth: 200,
              objectFit: "contain",
            }}
          />
        </div>
        <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto" }}>
          {table}
        </div>
      </div>
    </div>
  );
}

export function ProposalTermsHeader({ locationName }) {
  return (
    <div className="main-heading">
      <div className="row">
        <div className="col-md-4 order-1 order-md-2">
          <div className="sw-branding">
            <img
              src="https://clientportal.sworks.co.in/assets/images/SW_logo_black.svg"
              alt="SmartWorks"
              title="SmartWorks"
            />
          </div>
        </div>
        <div className="col-md-8 order-2 order-md-1">
          <div className="">
            <h2>Head Terms - Commercial Proposal</h2>
            <p className="">- {locationName}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProposalTermsTable({
  createdProposal,
  rowvalue,
  totalHour,
  selectedRange,
}) {
  const rows = [
    {
      name: "Client Requirement",
      value:
        createdProposal?.spaceUpdate === "Square Feet"
          ? createdProposal?.totalArea + " Square Feet"
          : createdProposal?.spaceUpdate === "Workstation"
            ? (createdProposal?.proposedWorkstation ||
                createdProposal?.workstations ||
                "") + " Workstation"
            : createdProposal?.spaceUpdate === "WholeSuite"
              ? formatSuiteSectionTitle(createdProposal)
              : "Virtual Seat",
      isRupee: false,
    },
    {
      name: "Requirement Breakup",
      value:
        createdProposal?.requirementBreakup ||
        createdProposal?.requirementBreakup ||
        "NA ",
      isRupee: false,
    },
    {
      name: "Floor",
      value:
        formatFloorForProposal(createdProposal || rowvalue) || " ",
      isRupee: false,
    },
    {
      name: "Contract Term",
      value: createdProposal?.contractTerm + " Months" || " ",
      isRupee: false,
    },
    {
      name: "Lock-in Period",
      value:
        (createdProposal?.lockinperiod || createdProposal?.lockInPeriod) +
          " Months" || " ",
      isRupee: false,
    },
    {
      name: "Notice Period",
      value:
        (createdProposal?.noticeperiod || createdProposal?.noticePeriod) +
          " Months post contract lock-in" || " ",
      isRupee: false,
    },
    {
      name: "Monthly Fixed Rental",
      value:
        rowvalue?.monthlyRental?.toLocaleString() + " + applicable taxes" ||
        " ",
      isRupee: true,
    },
    {
      name: "Security Deposit",
      value:
        createdProposal?.securityDeposit?.toLocaleString() || " ",
      isRupee: true,
    },
    {
      name: "Business Days & Hours",
      value:
        formatBusinessDaysHoursDisplay(
          createdProposal,
          selectedRange,
          totalHour
        ) || " ",
      isRupee: false,
    },
    {
      name: "No. of free four-wheeler parking",
      value: createdProposal?.noOfFreeFourWheelerParking || " ",
      isRupee: false,
    },
    {
      name: "No. of free two-wheeler parking",
      value: createdProposal?.noOfFreeTwoWheelerParking || " ",
      isRupee: false,
    },
    {
      name: "No. of chargeable four-wheeler parking",
      value: createdProposal?.noOfChargeableFourWheelerParking || " ",
      isRupee: false,
    },
    {
      name: "No. of chargeable two-wheeler parking",
      value: createdProposal?.noOfChargeableTwoWheelerParking || " ",
      isRupee: false,
    },
    {
      name: "Car Parking Charges",
      value:
        createdProposal?.carParkingCharges +
          " + applicable taxes / per car / per month" ||
        createdProposal?.carParkingCharges +
          " + applicable taxes / per car / per month" ||
        " ",
      isRupee: true,
    },
    {
      name: "Bike Parking Charges",
      value:
        createdProposal?.bikeParkingCharges +
          " + applicable taxes / per bike / per month" ||
        createdProposal?.bikeParkingCharges +
          " + applicable taxes / per bike / per month" ||
        " ",
      isRupee: true,
    },
    {
      name: "Notes Visible",
      value: " ",
      isRupee: false,
    },
    {
      name: "Notes",
      value: createdProposal?.notesToClient || " ",
      isRupee: false,
    },
  ];

  const visibleRows = rows.filter((data) => {
    if (data.value === " ") return false;
    if (String(data?.value)?.indexOf("null") !== -1) return false;
    if (String(data?.value)?.indexOf("undefined") !== -1) return false;
    return true;
  });

  return (
    <section className="py-3">
      <div className="container-fluid ProposalHeadTerms_zebra">
        {visibleRows.map((data, index) => (
          <div
            key={data.name}
            className="ProposalHeadTerms_row"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              background: index % 2 === 0 ? "#f2f2f2" : "#ffffff",
              padding: "14px 16px",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                flex: "0 0 42%",
                maxWidth: "42%",
                minWidth: 0,
                fontSize: "14px",
                color: "#222631",
              }}
            >
              {data.name}
            </div>
            <div
              style={{
                flex: "1 1 auto",
                minWidth: 0,
                fontSize: "14px",
                color: "#222631",
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
                ...(data.name === "Business Days & Hours"
                  ? { textTransform: "capitalize" }
                  : {}),
              }}
            >
              {data.name === "Notes" ? (
                data.value
              ) : (
                <>
                  {data.isRupee ? <span>S$</span> : null}
                  {data.isRupee ? " " : null}
                  {data.value}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ProposalTermsBlock({
  createdProposal,
  rowvalue,
  totalHour,
  selectedRange,
}) {
  return (
    <div className="mt-5">
      <div
        style={{
          paddingLeft: "0.8rem",
          marginTop: "2rem",
          marginRight: "0.8rem",
          marginLeft: "2rem",
          color: "#222631",
        }}
      >
        <ProposalTermsHeader locationName={createdProposal?.location?.name} />
        <ProposalTermsTable
          createdProposal={createdProposal}
          rowvalue={rowvalue}
          totalHour={totalHour}
          selectedRange={selectedRange}
        />
      </div>
    </div>
  );
}

export default ProposalTermsBlock;
